import {
    DevelopmentLocationTrackerLocationViewModel, SetLocationChainageCommand, SetLocationPlanningPriorityCommand,
    SLCLocationTrackerLocationViewModel, UpdateLocationDetailsCommand, UpdateLocationStatusCommand,
    UpdateLocationTriggerActionResponsePlanStatusCommand, UpsertLocationExtendedPropertyCommand
} from '@/models/api';
import { defineStore } from 'pinia';
import {
    ClientCommonLocationTracking,
    ClientDevelopmentLocationTrackerLocationViewModel,
    ClientLocationTrackingLocation
} from '@/models/client/location-tracker';
import { useLocationTrackerStore } from '@/lib/stores/LocationTrackerStore';
import Locations from '@/lib/data/Locations';
import LocationDetails from '@/lib/data/LocationDetails';
import { showToastMessage } from '@/lib/Toast';

function transformLocationViewModelToClientModel(location: DevelopmentLocationTrackerLocationViewModel): ClientDevelopmentLocationTrackerLocationViewModel {
    return {
        locationId: location.locationId,
        commonInformation: {
            ... location.commonInformation!
        },
        developmentInformation: location.developmentInformation!
    };
}

export const useDevelopmentLocationTrackerStore = defineStore('developmentLocationTracker', {
    state: () => {
        return {
            _departmentId: '',
            _mineAreaId: '',
            isInitialized: false,
            locations: [] as ClientDevelopmentLocationTrackerLocationViewModel[],
        };
    },
    actions: {
        async populateStore(mineId: string, departmentId: string, includeArchived: boolean = false): Promise<void> {
            this.isInitialized = false;
            this._departmentId = departmentId;
            this._mineAreaId = mineId;

            await this.populateLocations(departmentId, includeArchived);
            await useLocationTrackerStore().populateStore(mineId, departmentId, this.patchLocationCommonInformation);
            this.isInitialized = true;
        },
        patchLocationCommonInformation(locationId: string, patcher: (location: ClientCommonLocationTracking) => ClientCommonLocationTracking) {
            const locationIndex = this.locations.findIndex(l => l.locationId === locationId);
            if(locationIndex === -1) return;
            const location = this.locations[locationIndex];
            if (location != null && location.commonInformation != null) {
                this.locations.splice(locationIndex, 1, {
                    ...location,
                    commonInformation: patcher(location.commonInformation)
                });
            }
        },
        async populateLocations(departmentId: string, includeArchived: boolean = false): Promise<void> {
            const response =
                ((await Locations.GetDevelopmentLocationTrackerDetail(departmentId, includeArchived)) as DevelopmentLocationTrackerLocationViewModel[])
                ?? ([] as DevelopmentLocationTrackerLocationViewModel[]);
            if (response) {
                this.locations.splice(0, this.locations.length, ...response.map(transformLocationViewModelToClientModel));
            }
        },
        async executeSetLocationChainage(command: SetLocationChainageCommand){
            const result = await Locations.setLocationChainage(command);
            showToastMessage(result);

            await this.populateStore(this._mineAreaId, this._departmentId);
        },
        async executeDeleteLocationChainage(locationId: string, chainageId: string) {
            const result = await Locations.deleteLocationChainage(locationId, chainageId);
            showToastMessage(result);

            await this.populateStore(this._mineAreaId, this._departmentId);
        },
        async executeUpdateLocationTARPStatus(command: UpdateLocationTriggerActionResponsePlanStatusCommand) {
            await Locations.executeUpdateTARPStatusCommand(command);

            // force refresh
            await this.populateStore(this._mineAreaId, this._departmentId);
        },
        async executeUpdateLocationStatus(command: UpdateLocationStatusCommand) {
            await Locations.executeUpdateStatusCommand(command);

            const locationToUpdate = this.locations.find(
                (l: ClientDevelopmentLocationTrackerLocationViewModel) => l.locationId === command.locationId
            );

            if (locationToUpdate) locationToUpdate.commonInformation!.locationStatus = command.status;
        },
        async executeUpdateLocationDetails(command: UpdateLocationDetailsCommand) {
            await LocationDetails.send(command);

            const locationToUpdate = this.locations.find(l=>l.locationId === command.locationId);

            if(locationToUpdate != null && locationToUpdate.commonInformation != null) {
                locationToUpdate.commonInformation.details = {
                    _type: 'LocationTrackingLocationDetailsViewModel',
                    stockpileId: command.stockpileId,
                    materialDestinationId: command.materialDestinationId,
                    materialTypeId: command.materialTypeId
                };
            }
        },
        async executeSetPlanningPriority(command: SetLocationPlanningPriorityCommand) {
            await Locations.executeSetPlanningPriorityCommand(command);

            await this.populateLocations(this._departmentId);
        },
        async executeUpdateExtendedProperty(command: UpsertLocationExtendedPropertyCommand): Promise<{ id: string }> {
            const response = await Locations.ExecuteUpsertExtendedProperty(command);

            await this.populateLocations(this._departmentId);
            return response;
        },
    },
    getters: {

    }
});